/**
 * Live partner-config check (epic 085 D9, task 04): while a form is edited,
 * ask the server's dry-run route what it thinks of the partner slice the form
 * would save. The admin client holds no rule logic — it sends the request and
 * renders the issues it gets back.
 *
 * - The request is keyed by its JSON (the forms hold ids as strings, so no
 *   bigint ever reaches `JSON.stringify`). A new key waits `delayMs` before
 *   the call — a burst of edits sends one check for the settled request.
 * - Only the latest key's response is applied: a slower answer to an earlier
 *   request is dropped when it lands after a later one.
 * - Until the next answer lands the previous one stays on screen, so the panel
 *   does not flicker on every keystroke.
 * - `request === null` (nothing to check) → `[]` at once, no call.
 * - A failed check — rejected, or `check` throwing synchronously — → `null`
 *   ("render nothing"). The hook never throws and never logs the request.
 */

import { useEffect, useRef, useState } from 'react';
import type { PartnerConfigIssue } from '@leads-platform/shared';

const DEFAULT_DELAY_MS = 400;

/** Run one check; any failure, synchronous or not, becomes `null`. */
async function runCheck<T>(
  check: (req: T) => Promise<PartnerConfigIssue[]>,
  request: T
): Promise<PartnerConfigIssue[] | null> {
  try {
    return await check(request);
  } catch {
    return null;
  }
}

export function usePartnerConfigCheck<T>(
  request: T | null,
  check: (req: T) => Promise<PartnerConfigIssue[]>,
  delayMs: number = DEFAULT_DELAY_MS
): PartnerConfigIssue[] | null {
  const key = request === null ? null : JSON.stringify(request);
  const [issues, setIssues] = useState<PartnerConfigIssue[] | null>(null);

  // The latest request and check, read when the timer fires — the timer is
  // re-armed by a new key only, not by every render's fresh object.
  const requestRef = useRef(request);
  const checkRef = useRef(check);
  useEffect(() => {
    requestRef.current = request;
    checkRef.current = check;
  });

  useEffect(() => {
    if (key === null) {
      // Nothing to check: drop the last answer so a later request starts clean.
      setIssues(null);
      return undefined;
    }
    // Cleared by the cleanup when a newer key (or unmount) supersedes this one.
    let latest = true;
    const timer = window.setTimeout(() => {
      const current = requestRef.current;
      if (current === null) return;
      void runCheck(checkRef.current, current).then((next) => {
        if (latest) setIssues(next);
      });
    }, delayMs);
    return () => {
      latest = false;
      window.clearTimeout(timer);
    };
  }, [key, delayMs]);

  return key === null ? [] : issues;
}

/** True when the issues carry a blocker the save would refuse — one not grandfathered (D4). */
export function hasNewBlocker(issues: PartnerConfigIssue[] | null): boolean {
  return issues !== null && issues.some((i) => i.severity === 'block' && i.existing !== true);
}
